import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Lock, Crown, LogOut, ArrowUpRight } from "lucide-react";
import { libraryApi, paymentsApi } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { PublishingNav, PublishingFooter } from "@/components/publishing/PublishingShared";
import { BusinessInquiryModal } from "@/components/publishing/BusinessInquiryModal";

const BookCoverImg = ({ book, className = "" }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <div
      className={`relative overflow-hidden bg-black/40 ${className}`}
      style={{ height: '360px' }}
    >
      <img
        src={book.cover_image}
        alt={book.title_display}
        onLoad={() => setLoaded(true)}
        className={`w-full h-full object-contain transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at center, ${book.accent_color}15, transparent 70%)`,
          mixBlendMode: 'screen',
        }}
      />
    </div>
  );
};

export default function MyLibrary() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [purchasing, setPurchasing] = useState(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  const load = () => libraryApi.access().then(setData).catch(() => {});
  useEffect(() => { window.scrollTo(0, 0); load(); }, []);

  const buy = async (lookup_key) => {
    setPurchasing(lookup_key);
    try {
      const { checkout_url } = await paymentsApi.checkout(lookup_key);
      window.location.href = checkout_url;
    } catch (e) {
      setPurchasing(null);
      alert("Checkout failed. Please try again.");
    }
  };

  if (!data) {
    return (
      <div className="bg-primary min-h-screen flex items-center justify-center">
        <div className="text-white/30 font-body text-sm tracking-widest uppercase">Loading your library...</div>
      </div>
    );
  }

  const { books, access, pricing } = data;
  const ownedCount = books.filter(b => b.has_access).length;

  return (
    <div className="bg-primary min-h-screen">
      <div className="noise-overlay" />
      <PublishingNav />

      {/* Hero */}
      <section data-testid="my-library-hero" className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-jcrown-gold/10 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Crown className="w-5 h-5 text-jcrown-gold" />
                <p className="text-jcrown-gold/70 text-[10px] tracking-[0.4em] uppercase font-body">
                  {user?.name || user?.email} • {ownedCount}/{books.length} unlocked
                </p>
              </div>
              <h1 className="font-heading text-white text-5xl md:text-7xl lg:text-8xl tracking-tighter uppercase leading-[0.9]">
                MY LIBRARY
              </h1>
              <p className="mt-6 text-white/50 font-body text-lg">
                Your Johnny Crown digital collection.
              </p>
              {access.has_subscription && (
                <div className="mt-4 inline-flex items-center gap-2 border border-jcrown-gold/40 px-3 py-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-jcrown-gold animate-pulse" />
                  <span className="text-jcrown-gold font-body text-[10px] tracking-widest uppercase">Library Pass Active</span>
                </div>
              )}
              {access.has_collection && !access.has_subscription && (
                <div className="mt-4 inline-flex items-center gap-2 border border-jcrown-gold/40 px-3 py-1.5">
                  <Crown className="w-3 h-3 text-jcrown-gold" />
                  <span className="text-jcrown-gold font-body text-[10px] tracking-widest uppercase">Complete Collection Owner</span>
                </div>
              )}
            </div>
            <button
              onClick={() => { logout().then(() => window.location.href = '/'); }}
              data-testid="logout-btn"
              className="inline-flex items-center gap-2 border border-white/10 text-white/60 hover:text-white hover:border-white/30 px-5 py-2.5 font-body text-xs tracking-widest uppercase transition-all"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign Out
            </button>
          </div>
        </div>
      </section>

      {/* Books Grid */}
      <section data-testid="library-books" className="relative bg-primary pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {books.map(book => (
              <div key={book.id} data-testid={`library-book-${book.slug}`} className="flex flex-col">
                <div className="relative group mb-5">
                  <BookCoverImg book={book} />
                  {!book.has_access && (
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="text-center">
                        <Lock className="w-6 h-6 text-jcrown-gold mx-auto mb-2" />
                        <p className="text-white/70 font-body text-xs tracking-widest uppercase">Locked</p>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-body mb-1">{book.category}</p>
                <h3 className="font-heading text-white text-xl tracking-tight uppercase mb-1">{book.title_display}</h3>
                <p className="text-white/40 font-body text-xs mb-4">{book.author_credit}</p>
                <div className="mt-auto">
                {book.has_access ? (
                  <Link
                    to={`/read/${book.slug}`}
                    data-testid={`read-${book.slug}`}
                    className="w-full inline-flex items-center justify-center gap-2 bg-jcrown-gold text-jcrown-dark px-5 py-3 font-body text-xs tracking-widest uppercase hover:bg-white hover:text-black transition-all active:scale-95"
                  >
                    <BookOpen className="w-3.5 h-3.5" />
                    Read Now
                  </Link>
                ) : (
                  <button
                    onClick={() => buy(book.stripe_lookup_key)}
                    disabled={purchasing === book.stripe_lookup_key}
                    data-testid={`buy-${book.slug}`}
                    className="w-full inline-flex items-center justify-center gap-2 border border-white/20 text-white px-5 py-3 font-body text-xs tracking-widest uppercase hover:border-white hover:bg-white/5 transition-all active:scale-95 disabled:opacity-50"
                  >
                    <Lock className="w-3.5 h-3.5" />
                    {purchasing === book.stripe_lookup_key ? "Redirecting..." : `Unlock — $${book.digital_price}`}
                  </button>
                )}
                </div>
              </div>
            ))}
          </div>

          {/* Upsells */}
          {!access.has_collection && !access.has_subscription && (
            <div className="mt-24 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div data-testid="upsell-collection" className="border border-jcrown-gold/30 bg-black/40 p-8 md:p-10">
                <p className="text-jcrown-gold/70 text-[10px] tracking-[0.3em] uppercase font-body mb-3">Best Value</p>
                <h3 className="font-heading text-white text-3xl tracking-tight uppercase mb-3">Get the Complete Collection</h3>
                <p className="text-white/50 font-body text-sm mb-6">Unlock all 6 books permanently. Save ${pricing.collection_savings} vs individual.</p>
                <div className="mb-6">
                  <span className="text-white/30 font-body text-sm line-through mr-2">${pricing.collection_regular_value}</span>
                  <span className="font-heading text-white text-4xl tracking-tight">${pricing.complete_collection}</span>
                </div>
                <button
                  onClick={() => buy("complete_collection")}
                  disabled={purchasing === "complete_collection"}
                  data-testid="upsell-collection-btn"
                  className="w-full bg-jcrown-gold text-jcrown-dark px-6 py-3 font-body text-xs tracking-widest uppercase hover:bg-white hover:text-black transition-all active:scale-95 disabled:opacity-50"
                >
                  {purchasing === "complete_collection" ? "Redirecting..." : "Unlock All 6 Books"}
                </button>
              </div>
              <div data-testid="upsell-pass" className="border border-white/10 bg-black/40 p-8 md:p-10">
                <p className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-body mb-3">Membership</p>
                <h3 className="font-heading text-white text-3xl tracking-tight uppercase mb-3">Library Pass</h3>
                <p className="text-white/50 font-body text-sm mb-6">Access the entire growing library. Cancel anytime.</p>
                <div className="mb-6">
                  <span className="font-heading text-white text-4xl tracking-tight">${pricing.library_pass_monthly}</span>
                  <span className="text-white/40 font-body text-sm ml-2">/ month</span>
                </div>
                <button
                  onClick={() => buy("library_pass_monthly")}
                  disabled={purchasing === "library_pass_monthly"}
                  data-testid="upsell-pass-btn"
                  className="w-full border border-white/20 text-white px-6 py-3 font-body text-xs tracking-widest uppercase hover:border-white hover:bg-white/5 transition-all active:scale-95 disabled:opacity-50"
                >
                  {purchasing === "library_pass_monthly" ? "Redirecting..." : "Join the Library"}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <PublishingFooter onBusinessInquiryClick={() => setInquiryOpen(true)} />
      <BusinessInquiryModal isOpen={inquiryOpen} onClose={() => setInquiryOpen(false)} />
    </div>
  );
}
